import { useEffect, useState } from "react";
import { Api, ApiError, type AppNotification } from "../api.js";
import { useI18n } from "../i18n.js";
import { SiteRow } from "./BlockedSites.js";
import { ModelMigrationBanner } from "./ModelMigrationBanner.js";
import { Badge } from "./ui.js";

/** How long ago, in words. */
export function ago(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} minutes ago`;
  if (s < 86400) return `${Math.round(s / 3600)} hours ago`;
  return `${Math.round(s / 86400)} days ago`;
}

export function Notifications({ api }: { api: Api }) {
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState(24);
  const { t } = useI18n();
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let live = true;
    api.notifications(hours).then(
      (r) => live && setItems(r.notifications),
      (e) => live && setError(e instanceof ApiError ? e.message : "Could not load notifications."),
    );
    return () => {
      live = false;
    };
  }, [api, hours, reload]);

  return (
    <main className="page">
      <div className="row between">
        <h1>Notifications</h1>
        <label className="inline">
          Show the last{" "}
          <select aria-label="Time window" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            <option value={24}>24 hours</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days</option>
          </select>
        </label>
      </div>
      <p>Problems your chat window ran into on your websites, and leads that could not be delivered (the leads themselves are always saved). Your website itself is never affected when the chat has a problem: the window simply does not appear, or shows visitors a retry.</p>
      {error && <div className="banner warn" role="alert">{error}</div>}
      <p className="hint" aria-live="polite">{notice}</p>
      {items === null && !error && <p>Loading…</p>}
      {items?.length === 0 && (
        <div className="card empty">
          <h2>All quiet</h2>
          <p>No problems reported in this period.</p>
        </div>
      )}
      <ul className="notes">
        {items?.map((n) => n.actionRequired ? (
          <li key={n.id} className="card" data-severity={n.severity}>
            <strong>{n.agentName || "Untitled agent"}</strong>
            <ModelMigrationBanner api={api} agentId={n.agentId} note={n} onChanged={() => setReload((x) => x + 1)} />
            <a className="btn secondary" href={`#/agents/${n.agentId}`}>{t("mm.open")}</a>
          </li>
        ) : (
          <li key={n.id} className="card" data-severity={n.severity}>
            <div className="row between">
              <strong>{n.kind === "billing" ? "Billing" : n.agentName || "Untitled agent"}</strong>
              <span className="row">
                <Badge tone={n.severity === "error" ? "bad" : "warn"}>{n.severity === "error" ? "Needs attention" : "Heads up"}</Badge>
                <span className="hint">{ago(n.lastAt)}</span>
              </span>
            </div>
            {n.blockedSites?.length ? (
              <>
                <ul className="blocked-sites">
                  {n.blockedSites.map((b) => (
                    <SiteRow key={b.origin} api={api} agentId={n.agentId} label={t("blocked.triedNote", { origin: b.origin, agent: n.agentName })} sub={t("blocked.adds", { entries: b.entries.join(", ") })} what={{ origin: b.origin }} canAllow allowText={t("blocked.allow")} allowLabel={t("blocked.allowLabel", { origin: b.origin })} onDone={(note) => { setNotice(note); setReload((x) => x + 1); }} />
                  ))}
                </ul>
                <p className="hint">{n.advice}</p>
              </>
            ) : (
              <>
                <p>{n.message}</p>
                <p className="hint">{n.advice}</p>
              </>
            )}
            {n.kind === "billing" ? (
              <a className="btn secondary" href="#/billing">
                Open Billing
              </a>
            ) : n.kind === "lead_delivery_failed" ? (
              <a className="btn secondary" href="#/leads">
                Open Leads
              </a>
            ) : (
              <a className="btn secondary" href={`#/agents/${n.agentId}?step=launch`}>
                Open Launch settings
              </a>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}

/** Number of problems in the last day, for the badge in the top bar. Refreshes every minute and whenever the screen changes. Quiet on failure: a badge is not worth an error. */
export function useNotificationCount(api: Api, enabled: boolean, refreshKey: string, everyMs = 60_000): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const load = () => api.notifications(24).then((r) => live && setN(r.notifications.length), () => {});
    void load();
    const t = setInterval(load, everyMs);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [api, enabled, everyMs, refreshKey]);
  return n;
}
